import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ShopProvider } from "@/context/Shop";
import { StaffAuthProvider } from "@/context/StaffAuth";
import StoreLayout from "@/components/store/StoreLayout";
import AdminLayout from "@/components/admin/AdminLayout";

import Home from "@/pages/store/Home";
import GenderLanding from "@/pages/store/GenderLanding";
import Listing from "@/pages/store/Listing";
import ProductDetail from "@/pages/store/ProductDetail";
import Cart from "@/pages/store/Cart";
import Wishlist from "@/pages/store/Wishlist";
import Designers from "@/pages/store/Designers";
import Collections from "@/pages/store/Collections";
import Journal from "@/pages/store/Journal";
import Article from "@/pages/store/Article";
import Policy from "@/pages/store/Policy";
import Contact from "@/pages/store/Contact";
import NotFound from "@/pages/store/NotFound";

import AccountLayout from "@/pages/account/AccountLayout";
import AccountLogin from "@/pages/account/AccountLogin";
import AccountRegister from "@/pages/account/AccountRegister";
import AccountHome from "@/pages/account/AccountHome";
import AccountProfile from "@/pages/account/AccountProfile";
import AccountAddresses from "@/pages/account/AccountAddresses";
import AccountOrders from "@/pages/account/AccountOrders";
import AccountSecurity from "@/pages/account/AccountSecurity";
import AccountConsents from "@/pages/account/AccountConsents";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminProductEdit from "@/pages/admin/AdminProductEdit";
import AdminBrands from "@/pages/admin/AdminBrands";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminCollections from "@/pages/admin/AdminCollections";
import AdminSuppliers from "@/pages/admin/AdminSuppliers";
import AdminWarehouses from "@/pages/admin/AdminWarehouses";
import AdminInventory from "@/pages/admin/AdminInventory";
import AdminPurchaseOrders from "@/pages/admin/AdminPurchaseOrders";
import AdminPurchaseOrderDetail from "@/pages/admin/AdminPurchaseOrderDetail";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminRoles from "@/pages/admin/AdminRoles";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCustomerDetail from "@/pages/admin/AdminCustomerDetail";

function StoreApp() {
  return (
    <ShopProvider>
      <Outlet />
    </ShopProvider>
  );
}

function AdminApp() {
  return (
    <StaffAuthProvider>
      <Outlet />
    </StaffAuthProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-center" richColors={false} toastOptions={{ className: "font-sans" }} />
      <Routes>
        {/* Admin surface — separate provider, no storefront chrome */}
        <Route path="/admin" element={<AdminApp />}>
          <Route path="login" element={<AdminLogin />} />
          <Route element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="products/new" element={<AdminProductEdit />} />
            <Route path="products/:pid" element={<AdminProductEdit />} />
            <Route path="brands" element={<AdminBrands />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="collections" element={<AdminCollections />} />
            <Route path="suppliers" element={<AdminSuppliers />} />
            <Route path="warehouses" element={<AdminWarehouses />} />
            <Route path="inventory" element={<AdminInventory />} />
            <Route path="purchase-orders" element={<AdminPurchaseOrders />} />
            <Route path="purchase-orders/:poId" element={<AdminPurchaseOrderDetail />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="roles" element={<AdminRoles />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="customers/:cid" element={<AdminCustomerDetail />} />
          </Route>
        </Route>

        {/* Storefront surface */}
        <Route element={<StoreApp />}>
          <Route element={<StoreLayout />}>
            <Route index element={<Home />} />
            <Route path="women" element={<GenderLanding gender="women" />} />
            <Route path="men" element={<GenderLanding gender="men" />} />
            <Route path=":gender/:category" element={<Listing />} />
            <Route path="designers" element={<Designers />} />
            <Route path="designers/:slug" element={<Listing mode="designer" />} />
            <Route path="collections" element={<Collections />} />
            <Route path="collections/:slug" element={<Listing mode="collection" />} />
            <Route path="new-arrivals" element={<Listing mode="new" />} />
            <Route path="sale" element={<Listing mode="sale" />} />
            <Route path="search" element={<Listing mode="search" />} />
            <Route path="product/:slug" element={<ProductDetail />} />
            <Route path="bag" element={<Cart />} />
            <Route path="wishlist" element={<Wishlist />} />
            <Route path="journal" element={<Journal />} />
            <Route path="journal/:slug" element={<Article />} />
            <Route path="pages/contact" element={<Contact />} />
            <Route path="pages/delivery" element={<Policy slug="delivery" />} />
            <Route path="pages/returns" element={<Policy slug="returns" />} />
            <Route path="pages/privacy" element={<Policy slug="privacy" />} />
            <Route path="pages/terms" element={<Policy slug="terms" />} />
            <Route path="pages/cookies" element={<Policy slug="cookies" />} />
            <Route path="account/login" element={<AccountLogin />} />
            <Route path="account/register" element={<AccountRegister />} />
            <Route path="account" element={<AccountLayout />}>
              <Route index element={<AccountHome />} />
              <Route path="profile" element={<AccountProfile />} />
              <Route path="addresses" element={<AccountAddresses />} />
              <Route path="orders" element={<AccountOrders />} />
              <Route path="security" element={<AccountSecurity />} />
              <Route path="consents" element={<AccountConsents />} />
            </Route>
            {/* checkout placeholder: shows unavailable message */}
            <Route path="checkout" element={<Policy slug="checkout" />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
